import { useEffect, useState } from "react";
import { css } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { request as apiRequest } from "@sharpana/auth/api_client/allauth";
import { isMobileApp } from "@sharpana/shared/utils/web_environment";
import { notifyManageSubscription } from "@sharpana/shared/utils/webViewBridge";
import { RestorePurchases } from "./RestorePurchases";

const page = css`
  min-height: 100%;
  padding: 2rem 1rem 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  color: var(--color-text);
`;

const panel = css`
  width: 100%;
  max-width: 32rem;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 1.25rem;

  h2 {
    margin: 0 0 0.75rem;
    font-size: 1.05rem;
  }
`;

const row = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--color-border-subtle);

  &:last-of-type {
    border-bottom: none;
  }
`;

const label = css`
  color: var(--color-text-secondary);
`;

const value = css`
  font-weight: 600;
`;

const button = css`
  background: var(--color-primary);
  color: var(--color-on-primary);
  border: 1px solid var(--color-primary-border);
  border-radius: 8px;
  padding: 0.55rem 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--color-primary-hover);
  }
`;

const secondary = css`
  ${button};
  background: transparent;
  color: var(--color-text);
  border-color: var(--color-border-strong);

  &:hover {
    background: var(--color-bg-raised);
  }
`;

const actions = css`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

interface BalanceResponse {
  success?: boolean;
  has_active_subscription?: boolean;
  token_balance?: number;
}

/**
 * Account and billing settings for the coaching product.
 *
 * Two things here are load-bearing for the mobile app rather than for the
 * web: the store-managed subscription link (Apple and Google both require
 * that a subscription bought in-app is manageable from in-app) and the
 * restore-purchases action.
 *
 * The path itself is not contract — the wrapper tracks `/settings` only to
 * toggle a debug overlay that is currently commented out — but the page has
 * to be reachable, which is why CoachingHome links to it.
 */
export default function Settings() {
  const navigate = useNavigate();
  const inMobileApp = isMobileApp();
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data: any = await apiRequest(
          "GET",
          "/api/get-token-balance-and-subscription-status"
        );
        if (!cancelled) setBalance(data);
      } catch {
        if (!cancelled) setLoadFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div css={page}>
      <section css={panel}>
        <h2>Billing</h2>

        <div css={row}>
          <span css={label}>Subscription</span>
          <span css={value}>
            {loadFailed
              ? "—"
              : balance?.has_active_subscription
              ? "Active"
              : "None"}
          </span>
        </div>

        <div css={row}>
          <span css={label}>Credits</span>
          <span css={value}>
            {loadFailed ? "—" : (balance?.token_balance ?? 0)}
          </span>
        </div>

        <div css={actions}>
          <button css={button} onClick={() => navigate("/purchase")}>
            {balance?.has_active_subscription ? "Change plan" : "Get credits"}
          </button>

          {inMobileApp && balance?.has_active_subscription && (
            // Handled natively: the wrapper opens the App Store / Play
            // subscription screen, which is the only place a store-billed
            // subscription can actually be cancelled.
            <button css={secondary} onClick={notifyManageSubscription}>
              Manage in store
            </button>
          )}
        </div>
      </section>

      <RestorePurchases />
    </div>
  );
}
